import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Check, Search, Settings2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
// import { ButtonGroup as ModeSelection } from "../../../components/ui/button-group";
import { SliderWithLabel } from "@/components/ui/slider-with-label";
import AgentToolsList from "./agent-tools/agent-tools-list";
import { Model, PlaygroundParametersProps } from "@/types";
import { IDefaultModel } from "@/types/conversations";
import { cn } from "@/utils/cn";
import { useState } from "react";

/* Mode selection is hidden while Agentic is the only supported mode.
const modes = [
  {
    value: "agentic",
    labelKey: "playground.agentic",
    icon: <Sparkles className="h-3 w-3" />,
    tooltipKey: "playground.agenticTooltip",
  },
];
*/

type MobileProps = PlaygroundParametersProps & {
  models?: Array<Model>;
};

export const PlaygroundParametersMobile = ({
  models,
  selectedModel,
  temperature,
  maxTokens,
  // mode,
  language,
  onModelChange,
  // onModeChange,
  onTemperatureChange,
  onMaxTokensChange,
  onLanguageChange,
  leftActions,
  onOpenToolsMenu,
  defaultModels,
}: MobileProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const baseModel = models?.find((m) => m.id === selectedModel);

/*
  const currentModeLabel =
    modes.find((m) => m.value === mode)?.labelKey || "playground.agentic";
*/

  return (
    <div className="flex items-center justify-between gap-2 w-full">
      <style>{`
        @keyframes params-sheet-slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        @keyframes params-sheet-slide-down {
          from { transform: translateY(0); }
          to { transform: translateY(100%); }
        }
        [data-params-sheet][data-state="open"] {
          animation: params-sheet-slide-up 320ms cubic-bezier(0.22, 1, 0.36, 1);
        }
        [data-params-sheet][data-state="closed"] {
          animation: params-sheet-slide-down 220ms cubic-bezier(0.4, 0, 1, 1);
        }
      `}</style>
      <div className="flex items-center gap-1">
        {leftActions}
        <AgentToolsList onOpen={onOpenToolsMenu} />
      </div>

      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-2 rounded-full bg-secondary/60 hover:bg-secondary transition-colors text-foreground"
      >
        <Settings2 className="h-4 w-4" />
        <span className="text-xs font-medium truncate max-w-[120px]">
          {/* {t(currentModeLabel)} · */}
          {baseModel?.name ?? t("common.model")}
        </span>
      </button>

      <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-[1400] bg-black/50" />
          <DialogPrimitive.Content
            aria-describedby={undefined}
            data-params-sheet
            className={cn(
              "fixed bottom-0 inset-x-0 z-[1500] max-h-[85vh] overflow-y-auto",
              "bg-[var(--card-background)] rounded-t-2xl border-t border-border shadow-2xl",
            )}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 border-b border-border bg-[var(--card-background)]">
              <div className="w-8" />
              <DialogPrimitive.Title className="text-sm font-semibold">
                {t("playground.parameters")}
              </DialogPrimitive.Title>
              <DialogPrimitive.Close
                className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-secondary transition-colors"
                aria-label={t("common.close")}
              >
                <X className="h-4 w-4" />
              </DialogPrimitive.Close>
            </div>

            <div className="flex justify-center pt-2 pb-1">
              <div className="h-1 w-10 rounded-full bg-muted-foreground/30" />
            </div>

            <div className="px-4 py-4 flex flex-col gap-5 pb-[env(safe-area-inset-bottom,16px)]">
              {/*
              <section className="flex flex-col gap-2">
                <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {t("playground.mode")}
                </label>
                <ModeSelection
                  value={mode}
                  onValueChange={onModeChange}
                  options={modes.map((m) => ({
                    ...m,
                    label: t(m.labelKey),
                    icon: m.icon,
                    tooltip: t(m.tooltipKey),
                  }))}
                  className="w-fit grid-cols-1"
                />
              </section>
              */}

              <ModelPicker
                label={t("common.model")}
                models={models}
                selected={baseModel}
                onChange={onModelChange}
                type="base"
                defaultModels={defaultModels}
              />


              <section className="flex flex-col gap-1">
                <SliderWithLabel
                  label={t("playground.temperature")}
                  min={0}
                  max={1}
                  step={0.01}
                  value={temperature}
                  onChange={onTemperatureChange}
                />
              </section>

              <section className="flex flex-col gap-1">
                <SliderWithLabel
                  label={t("playground.maximumTokens")}
                  min={0}
                  max={baseModel?.max_tokens || 4096}
                  step={Math.max(
                    1,
                    Math.floor((baseModel?.max_tokens || 4096) / 100),
                  )}
                  value={maxTokens}
                  onChange={onMaxTokensChange}
                />
              </section>

              <section className="flex flex-col gap-2">
                <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {t("playground.language")}
                </label>
                <LanguagePicker language={language} onChange={onLanguageChange} />
              </section>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </div>
  );
};

const ModelPicker = ({
  label,
  models,
  selected,
  onChange,
  type,
  defaultModels,
}: {
  label: string;
  models?: Array<Model>;
  selected?: Model;
  onChange: (id: string) => void;
  type: "base" | "planner";
  defaultModels?: IDefaultModel[];
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const defaultId = defaultModels?.find((m) => m.mode === type)?.llm_config.id;
  const filteredModels = models?.filter((model) =>
    model.name
      .toLocaleLowerCase()
      .includes(searchQuery.trim().toLocaleLowerCase()),
  );

  return (
    <section className="flex flex-col gap-2">
      <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </label>
      <div className="rounded-lg border border-border p-1">
        <div className="relative mb-1">
          <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder={t("playground.searchPlaceholder")}
            aria-label={t("playground.searchPlaceholder")}
            className="h-9 w-full rounded-md border border-border bg-transparent ps-9 pe-3 text-sm text-foreground outline-hidden placeholder:text-muted-foreground focus:border-primary"
          />
        </div>
        <div className="flex max-h-[180px] flex-col gap-1 overflow-y-auto overscroll-contain">
          {filteredModels?.map((m) => {
            const isSelected = selected?.id === m.id;
            return (
            <button
              key={type + m.id}
              type="button"
              onClick={() => onChange(m.id)}
              className={cn(
                "flex items-center justify-between gap-2 px-3 py-2 rounded-md text-sm text-start transition-colors",
                {
                  "bg-primary/10 text-foreground font-medium": isSelected,
                  "hover:bg-secondary/60 text-foreground": !isSelected,
                },
              )}
            >
              <span className="truncate flex-1">{m.name}</span>
              <div className="flex items-center gap-2 shrink-0">
                {m.id === defaultId && (
                  <span className="bg-primary text-primary-foreground text-[10px] px-2 py-0.5 rounded-full font-medium">
                    {t("playground.default")}
                  </span>
                )}
                {isSelected && <Check className="h-4 w-4 text-primary" />}
              </div>
            </button>
            );
          })}
          {filteredModels?.length === 0 && (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">
              {t("usageAndCost.llmConfigs.noModelsFound")}
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

const LanguagePicker = ({
  language,
  onChange,
}: {
  language: string;
  onChange: (lang: string) => void;
}) => {
  const { t } = useTranslation();
  const options = [
    { value: "auto", label: t("playground.auto") },
    { value: "en", label: t("playground.english") },
    { value: "ar", label: t("playground.arabic") },
  ];

  return (
    <div className="grid grid-cols-3 gap-2">
      {options.map((opt) => {
        const active = opt.value === language;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={cn(
              "px-3 py-2 rounded-lg border text-sm transition-colors",
              active
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-transparent text-foreground border-border hover:bg-secondary/60",
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
};
